import React, { useContext, useEffect, useMemo, useState } from "react";
import loadable from "@loadable/component";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PageContext from "@micado-digital/react-ui/context/PageContext";
import CartButton from "@micado-digital/react-shop/views/ProductDetail01/CartButton";
import ProductDetailContext from "@micado-digital/react-shop/ProductDetail/context/ProductDetailContext";
import Availability from "@micado-digital/react-shop/views/ProductDetail01/Availability";
import CombinationSelection from "@micado-digital/react-shop/views/ProductDetail01/CombinationSelection";
import Gallery from "@micado-digital/react-shop/views/ProductDetail01/Gallery";
import ItemNumber from "@micado-digital/react-shop/views/ProductDetail01/ItemNumber";
import Price from "@micado-digital/react-shop/views/ProductDetail01/Price";
import ProductSelection from "@micado-digital/react-shop/views/ProductDetail01/ProductSelection";
import Quantity from "@micado-digital/react-shop/views/ProductDetail01/Quantity";
import Rating from "@micado-digital/react-shop/views/ProductDetail01/Rating";
import VoucherSelection from "@micado-digital/react-shop/views/ProductDetail01/VoucherSelection";
import getAvailability from "@micado-digital/react-shop/utils/getAvailability";
import Animation from "./Animation";
import Attributes from "./Attributes";
import Container from "../../components/Container";
import Description from "./Description";
import Descriptions from "./Descriptions";
import Documents from "./Documents";
import Information from "./Information";
import PreAnnouncement from "./PreAnnouncement";

import styles from "./ProductDetail.styles";

const Detail = loadable(() => import("@micado-digital/react-shop/ProductDetail"));
const Ratings = loadable(() => import("./Ratings"));
const Recommendations = loadable(() => import("./Recommendations"));

const Core = ({ lang }) => {
	const { productDetailData } = useContext(ProductDetailContext);
	const {
		available,
		availableCount,
		descriptions,
		documents,
		maxQuantity,
		ratingActive = false,
		referenceIDs,
		type
	} = productDetailData || {};
	const [fetchDate, setFetchDate] = useState(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const css = styles();

	const isAvailable = useMemo(() => {
		return getAvailability(available, availableCount);
	}, [available, availableCount]);

	return (
		<div className={clsx(css.productDetail, "mco-shop-product-detail")} data-type={type}>
			<Animation />
			<Container>
				{isMobile && (
					<div className={css.information}>
						<Information lang={lang} />
					</div>
				)}
				<div className={css.wrapper}>
					<div className={css.left}>
						<Description lang={lang} />
					</div>
					<div className={css.center}>
						<Gallery mediaFormats={{ xs: "product-detail" }} thumbsMediaFormat="" />
					</div>
					<div className={css.right}>
						<div className={css.price}>
							<Price lang={lang} />
						</div>
						<div className={css.attributes}>
							<Attributes lang={lang} />
						</div>
						<div className={css.itemNumber}>
							<ItemNumber />
						</div>
						<div className={css.availability}>
							<Availability isAvailable={isAvailable} count={availableCount} lang={lang} />
						</div>
						<div className={css.preAnnouncement}>
							<PreAnnouncement lang={lang} />
						</div>
						{ratingActive && (
							<div className={css.rating}>
								<Rating key={fetchDate} />
							</div>
						)}
						<ProductSelection lang={lang}>
							<div className={css.combinationSelection}>
								{type === "voucher" ? (
									<VoucherSelection lang={lang} />
								) : (
									<CombinationSelection lang={lang} />
								)}
							</div>
							{isAvailable && (
								<div className={css.buttons}>
									{type !== "voucher" && <Quantity max={maxQuantity ?? availableCount} />}
									<CartButton lang={lang} />
								</div>
							)}
						</ProductSelection>
					</div>
				</div>
				{!isEmpty(descriptions) && (
					<div className={css.descriptions}>
						<Descriptions lang={lang} />
					</div>
				)}
				{!isEmpty(documents) && type !== "voucher" && (
					<div className={css.documents}>
						<Documents lang={lang} />
					</div>
				)}
				{ratingActive && (
					<div className={css.ratings}>
						<Ratings key={fetchDate} lang={lang} setFetchDate={setFetchDate} />
					</div>
				)}
				{!isEmpty(referenceIDs) && (
					<div className={css.recommendations}>
						<Recommendations lang={lang} />
					</div>
				)}
			</Container>
		</div>
	);
};

const ProductDetail = ({ defaultLang = "de" }) => {
	const [pageData] = useContext(PageContext);
	const { lang: pageDataLang, setupDetails: data } = pageData || {};
	const [lang, setLang] = useState(defaultLang);

	useEffect(() => {
		if (!pageDataLang) return;

		setLang(pageDataLang);
	}, [pageDataLang]);

	return (
		<Detail
			data={data ? JSON.parse(data) : null}
			lang={lang}
			pagename={window.location.pathname}
		>
			<Core lang={lang} />
		</Detail>
	);
};

export default ProductDetail;
